/**
 * @internal
 * Internal styles for Command component.
 * Do not import this file from outside the Command component directory.
 */
import { styled, StyleSystemProps, keyframes } from '@withjoy/joykit';
import { Box } from '../Box';
import { animationTransition } from '@shared/utils/animationTransition';

// ============================================================================
// Styled components
// ============================================================================

export const StyledCommandRoot = styled(Box)``;
export const StyledCommandInputWrapper = styled(Box)``;
export const StyledCommandInputIcon = styled(Box)``;
export const StyledCommandInput = styled(Box)``;
export const StyledCommandList = styled(Box)``;
export const StyledCommandEmpty = styled(Box)``;
export const StyledCommandGroup = styled(Box)``;
export const StyledCommandGroupHeading = styled(Box)``;
export const StyledCommandItem = styled(Box)``;
export const StyledCommandSeparator = styled(Box)``;
export const StyledCommandShortcut = styled(Box)``;

// ============================================================================
// Base styles
// ============================================================================

export const commandRootStyles: StyleSystemProps = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: '12px',
  backgroundColor: 'white',
  width: '100%',
  height: '100%',
};

export const commandInputWrapperStyles: StyleSystemProps = {
  display: 'flex',
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'mono3',
  paddingX: 4,
};

export const commandInputIconStyles: StyleSystemProps = {
  marginRight: 3,
  flexShrink: 0,
  color: 'mono14',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

export const commandInputStyles: StyleSystemProps = {
  display: 'flex',
  height: '44px',
  width: '100%',
  backgroundColor: 'transparent',
  paddingY: 3,
  typographyVariant: 'body2',
  outline: 'none',
  border: 'none',
  _placeholder: {
    color: 'mono6',
  },
  _disabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
};

export const commandListStyles: StyleSystemProps = {
  position: 'relative',
  maxHeight: '300px',
  overflowY: 'auto',
  overflowX: 'hidden',
};

export const commandEmptyStyles: StyleSystemProps = {
  paddingY: 6,
  textAlign: 'center',
  typographyVariant: 'body2',
  color: 'mono8',
};

export const commandGroupStyles: StyleSystemProps = {
  overflow: 'hidden',
  padding: 2,
  color: 'mono14',
};

export const commandGroupHeadingStyles: StyleSystemProps = {
  paddingX: 2,
  paddingY: 2,
  typographyVariant: 'label3',
  fontWeight: 500,
  color: 'mono8',
};

export const commandItemStyles: StyleSystemProps = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  cursor: 'pointer',
  userSelect: 'none',
  borderRadius: '8px',
  paddingX: 2,
  paddingY: 2,
  typographyVariant: 'body2',
  outline: 'none',
  transition: animationTransition('background-color', 'color'),
};

export const commandItemSelectedStyles: StyleSystemProps = {
  backgroundColor: 'mono2',
};

export const commandItemDisabledStyles: StyleSystemProps = {
  pointerEvents: 'none',
  opacity: 0.5,
};

export const commandSeparatorStyles: StyleSystemProps = {
  marginX: -2,
  height: '1px',
  backgroundColor: 'mono3',
};

export const commandShortcutStyles: StyleSystemProps = {
  marginLeft: 'auto',
  typographyVariant: 'label3',
  letterSpacing: 'widest',
  color: 'mono8',
};

// ============================================================================
// Dialog-specific styles
// ============================================================================

/**
 * Dialog root styles - transparent so the glass effect on Modal.Content shows through.
 */
export const commandDialogRootStyles: StyleSystemProps = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  width: '100%',
  backgroundColor: 'transparent',
};

/**
 * Glass styles applied to Modal.Content when used as a command dialog.
 */
export const commandDialogContentStyles: StyleSystemProps = {
  maxWidth: '500px',
  borderRadius: '12px',
  border: '1px solid #FFF',
  backgroundColor: 'rgba(251, 251, 251, 0.80)',
  backdropFilter: 'saturate(180%) blur(20px)',
  boxShadow: 'rgba(255, 255, 255, 0.1) 0px 1px 1px 0px inset, rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px',
  // Pin the dialog to the top so the search input stays fixed as results change height.
  // Overrides the parent flex container's alignItems="center" vertical centering.
  alignSelf: 'flex-start',
  marginTop: '15vh',
};

const commandDialogEnter = keyframes`
  from {
    transform: scale(0.96);
  }
`;

/**
 * Styled panel for the command dialog that adds:
 * - -webkit-backdrop-filter for Safari frosted-glass support
 * - Tooltip-like scale-up entrance animation
 */
export const StyledCommandDialogPanel = styled(Box)`
  -webkit-backdrop-filter: saturate(180%) blur(20px);
  animation: ${commandDialogEnter} 200ms cubic-bezier(0.16, 1, 0.3, 1) both;
`;
